/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, useImperativeHandle } from 'react';

import { jsx } from '@atlaskit/css';

import { RovoThinkingIndicator } from './ThinkingIndicator';
import { useRotatingText } from './useRotatingText';
import type { RotatingTextConfig } from './useRotatingText';

/**
 * Imperative handle exposed via `ref` on `RotatingThinkingIndicator`.
 */
export type RotatingThinkingIndicatorHandle = {
	/**
	 * Restart the rotation cycle from the beginning:
	 * resets to the initial title and re-starts the delay before rotating.
	 */
	resetRotation: () => void;
};

type RovoThinkingIndicatorPassthroughProps = {
	/** Static title shown during the initial delay and used as fallback. */
	title: string;
	isAnimated?: boolean;
	icon?: boolean | React.ReactNode;
	animation?: boolean;
	disableMorphingAnimation?: boolean;
	showEllipsis?: boolean;
	testId?: string;
};

export type RotatingThinkingIndicatorProps = RovoThinkingIndicatorPassthroughProps & {
	/**
	 * Configuration for the rotating title behaviour.
	 * The `title` prop is prepended to `config.texts` as the first item
	 * shown during the initial delay.
	 */
	rotatingTitleConfig: RotatingTextConfig;
};

/**
 * A composed wrapper around `RovoThinkingIndicator` that rotates the title
 * text after an initial delay.
 *
 * Use `ref.current.resetRotation()` to imperatively restart the rotation
 * cycle (e.g. when research steps change).
 *
 * When rotation is not needed, use `RovoThinkingIndicator` directly.
 */
export const RotatingThinkingIndicator: React.ForwardRefExoticComponent<
	RovoThinkingIndicatorPassthroughProps & {
		/**
		 * Configuration for the rotating title behaviour.
		 * The `title` prop is prepended to `config.texts` as the first item
		 * shown during the initial delay.
		 */
		rotatingTitleConfig: RotatingTextConfig;
	} & React.RefAttributes<RotatingThinkingIndicatorHandle>
> = forwardRef<RotatingThinkingIndicatorHandle, RotatingThinkingIndicatorProps>(
	function RotatingThinkingIndicator(
		{
			title,
			rotatingTitleConfig,
			isAnimated,
			icon,
			animation,
			disableMorphingAnimation,
			showEllipsis,
			testId,
		},
		ref,
	) {
		// Prepend the static title so it's displayed during the initial delay
		const textsWithTitle = React.useMemo(
			() => [title, ...rotatingTitleConfig.texts],
			[title, rotatingTitleConfig.texts],
		);

		const { text, reset } = useRotatingText({
			texts: textsWithTitle,
			initialDelayMs: rotatingTitleConfig.initialDelayMs,
			intervalMs: rotatingTitleConfig.intervalMs,
		});

		useImperativeHandle(
			ref,
			() => ({
				resetRotation: reset,
			}),
			[reset],
		);

		return (
			<RovoThinkingIndicator
				title={text}
				isAnimated={isAnimated}
				icon={icon}
				animation={animation}
				disableMorphingAnimation={disableMorphingAnimation}
				showEllipsis={showEllipsis}
				testId={testId}
			/>
		);
	},
);
